import React, { useState, useLayoutEffect, useRef, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import interact from "interactjs";
import {
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Box,
  Paper,
  Typography,
  CircularProgress,
  Tooltip,
  Alert,
  Switch,
  FormControlLabel,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
} from "@mui/material";
import { Delete, Save, BorderColor, ArrowBack, ArrowForward } from "@mui/icons-material";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;

const A4_WIDTH = 595;
const A4_HEIGHT = 842;
const DEFAULT_FIELD = { width: 120, height: 50, type: "signature" };

const clearInteractTransform = (el) => {
  if (!el) return;
  el.style.transform = "";
  el.style.webkitTransform = "";
  el.setAttribute("data-x", "0");
  el.setAttribute("data-y", "0");
};

const createField = ({ id, page, x, y, user, groupId }) => ({
  ...DEFAULT_FIELD,
  id,
  page,
  x,
  y,
  width: DEFAULT_FIELD.width,
  height: DEFAULT_FIELD.height,
  user,
  ...(groupId ? { applyAllGroupId: groupId } : {}),
});

const ToolbarIcon = ({ title, onClick, disabled, children }) => (
  <Tooltip title={title} arrow>
    <span>
      <IconButton color="inherit" onClick={onClick} disabled={disabled}>
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const PDFEditor = ({ file, onBack, onNext, loading, assignees }) => {
  const [selectedUser, setSelectedUser] = useState("");
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [placedFields, setPlacedFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [applyToAllPages, setApplyToAllPages] = useState(false);
  const [scale, setScale] = useState(1);

  const fieldRefs = useRef({});
  const pdfContainerRef = useRef(null);
  const scaleRef = useRef(1);
  const applyToAllPagesRef = useRef(false);
  applyToAllPagesRef.current = applyToAllPages;

  const fieldsOnPage = useMemo(
    () => placedFields.filter((field) => field.page === currentPage),
    [placedFields, currentPage]
  );
  const pageFieldIds = fieldsOnPage.map((field) => field.id).join(",");
  const canApplyToAll = applyToAllPages || (placedFields.length === 1 && numPages > 1);

  const updateField = (fieldId, getUpdates) => {
    setPlacedFields((prev) => {
      const target = prev.find((field) => field.id === fieldId);
      if (!target) return prev;

      const updates = getUpdates(target);
      const syncAll =
        applyToAllPagesRef.current && target.applyAllGroupId;

      return prev.map((field) => {
        if (syncAll && field.applyAllGroupId === target.applyAllGroupId) {
          return { ...field, ...updates };
        }
        return field.id === fieldId ? { ...field, ...updates } : field;
      });
    });
  };

  const handlePageLoadSuccess = (page) => {
    const nextScale = Math.min(
      A4_WIDTH / page.originalWidth,
      A4_HEIGHT / page.originalHeight,
      1
    );
    setScale(nextScale);
    scaleRef.current = nextScale;
  };

  const addSignatureField = () => {
    if (!pdfContainerRef.current || !selectedUser || applyToAllPages) return;

    const rect = pdfContainerRef.current.getBoundingClientRect();
    setPlacedFields((prev) => [
      ...prev,
      createField({
        id: Date.now(),
        page: currentPage,
        x: rect.width / 4 - 50,
        y: rect.height / 4 - 20,
        user: selectedUser,
      }),
    ]);
  };

  const deleteField = () => {
    if (selectedField == null) return;

    setPlacedFields((prev) => {
      const target = prev.find((field) => field.id === selectedField);
      if (!target) return prev;

      if (applyToAllPages && target.applyAllGroupId) {
        setApplyToAllPages(false);
        return [];
      }
      return prev.filter((field) => field.id !== selectedField);
    });
    setSelectedField(null);
  };

  const handleApplyToAllPagesChange = (checked) => {
    if (!numPages) return;

    if (checked) {
      const source =
        placedFields.find((field) => field.id === selectedField) ||
        placedFields[0];
      if (!source) return;

      const groupId = Date.now();
      const duplicated = Array.from({ length: numPages }, (_, index) => {
        const page = index + 1;
        const { applyAllGroupId, ...rest } = source;
        return {
          ...rest,
          id: groupId + page,
          page,
          applyAllGroupId: groupId,
        };
      });

      setPlacedFields(duplicated);
      setSelectedField(
        duplicated.find((field) => field.page === currentPage)?.id ??
          duplicated[0].id
      );
      setApplyToAllPages(true);
      return;
    }

    const keep =
      placedFields.find((field) => field.page === currentPage) ||
      placedFields[0];
    if (keep) {
      const { applyAllGroupId, ...rest } = keep;
      setPlacedFields([rest]);
      setSelectedField(rest.id);
    }
    setApplyToAllPages(false);
  };

  const handlePageChange = (direction) => {
    setCurrentPage((prev) => {
      const next = prev + direction;
      return next >= 1 && next <= numPages ? next : prev;
    });
  };

  // Bind interact only when fields on the current page change (not on every drag update).
  useLayoutEffect(() => {
    const ids = pageFieldIds ? pageFieldIds.split(",").map(Number) : [];
    const boundEls = [];

    ids.forEach((id) => {
      const el = fieldRefs.current[id];
      if (!el) return;
      boundEls.push(el);

      interact(el).unset();
      interact(el)
        .draggable({
          inertia: false,
          modifiers: [
            interact.modifiers.restrictRect({
              restriction: pdfContainerRef.current,
            }),
          ],
          listeners: {
            start: (event) => clearInteractTransform(event.target),
            move: (event) => {
              const fieldId = Number(event.target.getAttribute("data-id"));
              clearInteractTransform(event.target);
              updateField(fieldId, (field) => ({
                x: field.x + event.dx / scaleRef.current,
                y: field.y + event.dy / scaleRef.current,
              }));
            },
            end: (event) => clearInteractTransform(event.target),
          },
        })
        .resizable({
          edges: { left: true, right: true, top: true, bottom: true },
          listeners: {
            move: (event) => {
              const fieldId = Number(event.target.getAttribute("data-id"));
              clearInteractTransform(event.target);
              updateField(fieldId, () => ({
                width: event.rect.width / scaleRef.current,
                height: event.rect.height / scaleRef.current,
              }));
            },
          },
        });
    });

    return () => {
      boundEls.forEach((el) => interact(el).unset());
    };
  }, [currentPage, pageFieldIds]);

  const applyHint = (() => {
    if (placedFields.length === 0) {
      return "Add a signature to enable this option";
    }
    if (!applyToAllPages && placedFields.length > 1) {
      return "Only available when exactly one signature is placed";
    }
    if (applyToAllPages) {
      return `Signature placed on all ${numPages} pages. Move or resize on any page to update all. Turn off to keep only the current page.`;
    }
    return null;
  })();

  return (
    <Paper sx={{ display: "flex", minHeight: "100vh" }} elevation={0}>
      <Paper
        sx={{ width: 240, padding: 2, display: "flex", flexDirection: "column" }}
        elevation={1}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Signature Fields
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="user-select-label">Assignee</InputLabel>
          <Select
            labelId="user-select-label"
            id="user-select"
            value={selectedUser}
            label="Assignee"
            onChange={(e) => setSelectedUser(e.target.value)}
          >
            {assignees?.map((assignee, index) => (
              <MenuItem key={index} value={assignee.name}>
                {assignee.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<BorderColor />}
          sx={{ mb: 2, justifyContent: "start" }}
          onClick={addSignatureField}
          disabled={!selectedUser || applyToAllPages}
        >
          Add Signature
        </Button>

        <Alert severity="info" sx={{ fontSize: "12px", mb: 2 }}>
          Select an assignee and click "Add Signature" to place signature
          positions on the PDF.
        </Alert>

        <FormControlLabel
          control={
            <Switch
              checked={applyToAllPages}
              onChange={(e) => handleApplyToAllPagesChange(e.target.checked)}
              disabled={!canApplyToAll}
            />
          }
          label="Apply signature to all pages"
          sx={{
            mb: 1,
            alignItems: "flex-start",
            "& .MuiFormControlLabel-label": { fontSize: "13px" },
          }}
        />

        {applyHint && (
          <Alert
            severity={applyToAllPages ? "success" : "info"}
            sx={{ mb: 2, fontSize: "11px" }}
          >
            {applyHint}
          </Alert>
        )}
      </Paper>

      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <AppBar
          position="static"
          elevation={1}
          sx={{ color: "primary.main", backgroundColor: "white" }}
        >
          <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
            <ToolbarIcon title="Go Back" onClick={onBack}>
              <ArrowBack />
            </ToolbarIcon>

            <Box>
              <ToolbarIcon
                title="Previous Page"
                onClick={() => handlePageChange(-1)}
                disabled={currentPage === 1}
              >
                <ArrowBack />
              </ToolbarIcon>

              <Typography variant="body1" component="span" sx={{ mx: 1 }}>
                Page {currentPage} of {numPages}
              </Typography>

              <ToolbarIcon
                title="Next Page"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === numPages}
              >
                <ArrowForward />
              </ToolbarIcon>
            </Box>

            <Box>
              <ToolbarIcon
                title={
                  applyToAllPages
                    ? "Delete signature from all pages"
                    : "Delete Selected Field"
                }
                onClick={deleteField}
                disabled={!selectedField}
              >
                <Delete />
              </ToolbarIcon>

              <ToolbarIcon
                title="Create Approval"
                onClick={() => onNext([...placedFields])}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <Save />
                )}
              </ToolbarIcon>
            </Box>
          </Toolbar>
        </AppBar>

        <Box
          ref={pdfContainerRef}
          sx={{
            flexGrow: 1,
            position: "relative",
            overflow: "hidden",
            p: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            my: 2,
            border: 2,
          }}
        >
          <Document
            file={file}
            onLoadSuccess={({ numPages: total }) => setNumPages(total)}
          >
            <Page
              pageNumber={currentPage}
              scale={scale}
              onLoadSuccess={handlePageLoadSuccess}
            />
          </Document>

          {fieldsOnPage.map((field) => (
            <Box
              key={field.id}
              ref={(el) => {
                fieldRefs.current[field.id] = el;
              }}
              data-id={field.id}
              className="pdf-field"
              onClick={() => setSelectedField(field.id)}
              sx={{
                position: "absolute",
                left: `${field.x}px`,
                top: `${field.y}px`,
                width: `${field.width}px`,
                height: `${field.height}px`,
                background: "rgba(255, 255, 0, 0.8)",
                padding: "5px",
                cursor: "move",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                border:
                  selectedField === field.id
                    ? "2px solid blue"
                    : "1px solid black",
                fontSize: "12px",
                zIndex: selectedField === field.id ? 10 : 1,
                borderRadius: "4px",
              }}
            >
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{ textAlign: "center" }}
              >
                {field.user}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: "center" }}>
                ✒️ Signature
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default PDFEditor;
